import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import {
  STATUS_INTERVIEW,
  INTERVIEW_COLORS,
  INTERVIEW_STATUS_LIST,
  HR_OVERALL_FEEDBACK,
  InterviewType
} from '../constants/common';

export const getExcelStatusFormatter = (row) => {
  const rawStatus =
    row?.status !== undefined && row?.status !== null && row?.status !== ''
      ? row.status
      : row?.Status !== undefined && row?.Status !== null && row?.Status !== ''
      ? row.Status
      : row?.interviewStatus !== undefined &&
        row?.interviewStatus !== null &&
        row?.interviewStatus !== ''
      ? row.interviewStatus
      : row?.roundStatus;

  const numStatus = Number(rawStatus);
  const statusObj = !isNaN(numStatus)
    ? INTERVIEW_STATUS_LIST.find((s) => s.code === numStatus)
    : INTERVIEW_STATUS_LIST.find((s) => s.name?.toLowerCase() === String(rawStatus).toLowerCase());

  const val = statusObj ? statusObj.name : rawStatus;
  const statusCode = !isNaN(numStatus) ? numStatus : statusObj ? statusObj.code : NaN;
  let color = INTERVIEW_COLORS.PENDING;

  if (!isNaN(statusCode)) {
    switch (statusCode) {
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE:
      case STATUS_INTERVIEW.MANAGEMENT_SCHEDULE:
      case STATUS_INTERVIEW.PRACTICAL_SCHEDULE:
      case STATUS_INTERVIEW.HR_SCHEDULE:
        color = INTERVIEW_COLORS.SCHEDULE;
        break;
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE:
      case STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE:
      case STATUS_INTERVIEW.HR_RESCHEDULE:
      case STATUS_INTERVIEW.PRACTICAL_RESCHEDULE:
        color = INTERVIEW_COLORS.RESCHEDULE;
        break;
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE:
      case STATUS_INTERVIEW.MANAGEMENT_COMPLETE:
      case STATUS_INTERVIEW.PRACTICAL_COMPLETE:
      case STATUS_INTERVIEW.HR_COMPLETE:
        color = INTERVIEW_COLORS.COMPLETE;
        break;
      case STATUS_INTERVIEW.CANCELLED:
        color = INTERVIEW_COLORS.CANCEL;
        break;
      default:
        color = INTERVIEW_COLORS.PENDING;
        break;
    }
  } else if (typeof rawStatus === 'string') {
    const s = rawStatus.toLowerCase();
    if (s.includes('reschedule')) {
      color = INTERVIEW_COLORS.RESCHEDULE;
    } else if (s.includes('schedule')) {
      color = INTERVIEW_COLORS.SCHEDULE;
    } else if (s.includes('complete')) {
      color = INTERVIEW_COLORS.COMPLETE;
    } else if (s.includes('cancel')) {
      color = INTERVIEW_COLORS.CANCEL;
    } else {
      color = INTERVIEW_COLORS.PENDING;
    }
  }

  return { value: val, color: color.replace('#', 'FF') };
};

export const getScheduleResultDisplay = (row) => {
  if (!row) return '--';

  const rawRound =
    row.interviewRound !== null && row.interviewRound !== undefined
      ? row.interviewRound
      : row.InterviewRound;
  const isHr =
    Number(rawRound) === InterviewType['HR Round'] ||
    (typeof row.stage === 'string' && row.stage.toLowerCase().includes('hr'));

  const rawResult =
    row.result !== null && row.result !== undefined && row.result !== ''
      ? String(row.result).trim()
      : row.Result !== null && row.Result !== undefined && row.Result !== ''
      ? String(row.Result).trim()
      : '';

  if (isHr) {
    if (rawResult === '1' || rawResult.toLowerCase() === 'selected') {
      const rawRatings =
        row.ratings !== null && row.ratings !== undefined ? row.ratings : row.Ratings;

      let ratings = {};
      if (typeof rawRatings === 'object' && rawRatings !== null) {
        ratings = rawRatings;
      } else if (typeof rawRatings === 'string') {
        try {
          ratings = JSON.parse(rawRatings);
        } catch (e) {
          ratings = {};
        }
      }

      const ratingResult =
        ratings?.ratingResult?.toString() ||
        ratings?.Result?.toString() ||
        ratings?.result?.toString() ||
        ratings?.OverAllFeedback?.toString();

      if (ratingResult) {
        const fb = HR_OVERALL_FEEDBACK.find(
          (f) =>
            f.code === String(ratingResult) ||
            f.name.toLowerCase() === String(ratingResult).toLowerCase()
        );
        return fb ? fb.name : ratingResult;
      }
      return 'Selected';
    }

    if (rawResult) {
      const fb = HR_OVERALL_FEEDBACK.find(
        (f) => f.code === rawResult || f.name.toLowerCase() === rawResult.toLowerCase()
      );
      return fb ? fb.name : rawResult;
    }
    return '--';
  }

  if (!rawResult) return '--';
  const fb = HR_OVERALL_FEEDBACK.find(
    (f) => f.code === rawResult || f.name.toLowerCase() === rawResult.toLowerCase()
  );
  return fb ? fb.name : rawResult;
};

export const getExcelResultFormatter = (row) => {
  return getScheduleResultDisplay(row);
};

/**
 * Exports data to a proper styled Excel (.xlsx) file using exceljs.
 * Preserves custom formatting and prevents MS Excel corruption warnings.
 *
 * @param {Object} params
 * @param {string} params.fileName - Name of the downloaded file (without extension)
 * @param {string} params.sheetName - Title of the report sheet
 * @param {Array} params.columns - Column configurations: [{ header: string, key: string, formatter?: function }]
 * @param {Array} params.data - The data rows to export
 * @param {string} params.activePill - Active duration filter pill name
 * @param {Array} params.customRange - Calendar custom range dates [start, end]
 * @param {Array} params.techFilter - Selected technology IDs
 * @param {Array} params.technologyListData - Full technology list for label lookup
 * @param {string} [params.hrName] - Name of the recruiter (optional)
 */
export const exportToExcelHtml = async ({
  fileName,
  sheetName,
  columns,
  data,
  activePill,
  customRange,
  techFilter,
  technologyListData,
  hrName
}) => {
  // 1. Format metadata details with actual dates and day names
  let durationText = activePill;
  if (activePill === 'Today') {
    durationText = dayjs().format('DD/MM/YYYY (dddd)');
  } else if (activePill === 'This Week') {
    const start = dayjs().startOf('week').format('DD/MM/YYYY (dddd)');
    const end = dayjs().endOf('week').format('DD/MM/YYYY (dddd)');
    durationText = `${start} - ${end}`;
  } else if (activePill === 'This Month') {
    const start = dayjs().startOf('month').format('DD/MM/YYYY (dddd)');
    const end = dayjs().endOf('month').format('DD/MM/YYYY (dddd)');
    durationText = `${start} - ${end}`;
  } else if (activePill === 'Custom Range' && customRange && customRange[0]) {
    const start = dayjs(customRange[0]).format('DD/MM/YYYY (dddd)');
    const end = customRange[1] ? dayjs(customRange[1]).format('DD/MM/YYYY (dddd)') : start;
    durationText = start === end ? start : `${start} - ${end}`;
  }

  let techText = 'All';
  if (techFilter && techFilter.length > 0 && technologyListData) {
    techText = techFilter
      .map((id) => {
        const found = technologyListData.find((tech) => tech.ID === id);
        return found ? found.Technology : id;
      })
      .join(', ');
  }

  // 2. Initialize Workbook and Worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Report');

  const colCount = columns.length;
  let currentRow = 1;

  // Title Row
  worksheet.mergeCells(currentRow, 1, currentRow, colCount);
  const titleCell = worksheet.getCell(currentRow, 1);
  titleCell.value = sheetName;
  titleCell.font = { size: 16, bold: true, color: { argb: 'FF7367F0' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  worksheet.getRow(currentRow).height = 30;
  currentRow++;

  // Recruiter Row
  if (hrName) {
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const hrCell = worksheet.getCell(currentRow, 1);
    hrCell.value = `Recruiter: ${hrName}`;
    hrCell.font = { size: 11, bold: true, color: { argb: 'FF1E293B' } };
    currentRow++;
  }

  // Duration Row
  if (durationText) {
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const durationCell = worksheet.getCell(currentRow, 1);
    durationCell.value = `Duration: ${durationText}`;
    durationCell.font = { size: 10, color: { argb: 'FF475569' } };
    currentRow++;
  }

  // Technology Row
  if (techFilter && techFilter.length > 0 && techText) {
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const techCell = worksheet.getCell(currentRow, 1);
    techCell.value = `Technology: ${techText}`;
    techCell.font = { size: 10, color: { argb: 'FF475569' } };
    currentRow++;
  }

  // Alignment helper
  const getAlignment = (col) => {
    const key = String(col.key || '').toLowerCase();
    const header = String(col.header || '').toLowerCase();
    if (
      key.includes('date') ||
      key.includes('status') ||
      key === 'count' ||
      key === 'total' ||
      header.includes('date') ||
      header.includes('status') ||
      header.includes('count') ||
      header.includes('total')
    ) {
      return { vertical: 'middle', horizontal: 'center' };
    }
    return { vertical: 'middle', horizontal: 'left' };
  };

  // Header Row
  const headerRow = worksheet.getRow(currentRow);
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = col.header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF7367F0' }
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };
    cell.alignment = getAlignment(col);
  });
  headerRow.height = 25;
  currentRow++;

  // Data Rows
  data.forEach((row, rowIndex) => {
    const dataRow = worksheet.getRow(currentRow);
    const isZebra = rowIndex % 2 === 1;
    const bgColor = isZebra ? 'FFF8FAFC' : 'FFFFFFFF';

    columns.forEach((col, colIndex) => {
      let val = '';
      let customColor = null;
      if (col.formatter) {
        const res = col.formatter(row);
        if (res !== null && typeof res === 'object' && res.value !== undefined) {
          val = res.value;
          customColor = res.color;
        } else {
          val = res;
        }
      } else {
        val = row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : '--';
      }

      // Convert val to string and unescape HTML entities if any exist
      let displayVal = String(val)
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

      const cell = dataRow.getCell(colIndex + 1);
      cell.value = displayVal;
      if (customColor) {
        cell.font = { color: { argb: customColor }, bold: true };
      } else {
        cell.font = { color: { argb: 'FF334155' } };
      }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: bgColor }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
      };
      cell.alignment = getAlignment(col);
    });
    currentRow++;
  });

  // Calculate dynamic column widths
  columns.forEach((col, index) => {
    let maxLen = col.header ? col.header.length : 12;
    data.forEach((row) => {
      let val = '';
      if (col.formatter) {
        val = col.formatter(row);
      } else {
        val = row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : '--';
      }
      const valLen = String(val).length;
      if (valLen > maxLen) {
        maxLen = valLen;
      }
    });
    // Set width (min 12, max 60)
    worksheet.getColumn(index + 1).width = Math.min(Math.max(maxLen + 2, 12), 60);
  });

  // Generate buffer and save as .xlsx
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  saveAs(blob, `${fileName}.xlsx`);
};

/**
 * Exports multiple datasets into separate sheets within a single Excel (.xlsx) file using exceljs.
 *
 * @param {Object} params
 * @param {string} params.fileName - Name of the downloaded file (without extension)
 * @param {Array} params.sheets - Array of sheet configurations: [{ sheetName, columns, data }]
 * @param {string} params.activePill - Active duration filter pill name
 * @param {Array} params.customRange - Calendar custom range dates [start, end]
 * @param {Array} params.techFilter - Selected technology IDs
 * @param {Array} params.technologyListData - Full technology list for label lookup
 * @param {string} [params.hrName] - Name of the recruiter (optional)
 */
export const exportMultipleSheetsToExcelHtml = async ({
  fileName,
  sheets,
  activePill,
  customRange,
  techFilter,
  technologyListData,
  hrName
}) => {
  // 1. Format metadata details
  let durationText = activePill;
  if (activePill === 'Today') {
    durationText = dayjs().format('DD/MM/YYYY (dddd)');
  } else if (activePill === 'This Week') {
    const start = dayjs().startOf('week').format('DD/MM/YYYY (dddd)');
    const end = dayjs().endOf('week').format('DD/MM/YYYY (dddd)');
    durationText = `${start} - ${end}`;
  } else if (activePill === 'This Month') {
    const start = dayjs().startOf('month').format('DD/MM/YYYY (dddd)');
    const end = dayjs().endOf('month').format('DD/MM/YYYY (dddd)');
    durationText = `${start} - ${end}`;
  } else if (activePill === 'Custom Range' && customRange && customRange[0]) {
    const start = dayjs(customRange[0]).format('DD/MM/YYYY (dddd)');
    const end = customRange[1] ? dayjs(customRange[1]).format('DD/MM/YYYY (dddd)') : start;
    durationText = start === end ? start : `${start} - ${end}`;
  }

  let techText = 'All';
  if (techFilter && techFilter.length > 0 && technologyListData) {
    techText = techFilter
      .map((id) => {
        const found = technologyListData.find((tech) => tech.ID === id);
        return found ? found.Technology : id;
      })
      .join(', ');
  }

  // 2. Initialize Workbook
  const workbook = new ExcelJS.Workbook();

  // Alignment helper
  const getAlignment = (col) => {
    const key = String(col.key || '').toLowerCase();
    const header = String(col.header || '').toLowerCase();
    if (
      key.includes('date') ||
      key.includes('status') ||
      key === 'count' ||
      key === 'total' ||
      header.includes('date') ||
      header.includes('status') ||
      header.includes('count') ||
      header.includes('total')
    ) {
      return { vertical: 'middle', horizontal: 'center' };
    }
    return { vertical: 'middle', horizontal: 'left' };
  };

  sheets.forEach((sheetConfig) => {
    const { sheetName, columns, data } = sheetConfig;
    const worksheet = workbook.addWorksheet(sheetName.substring(0, 31)); // Max length for sheet name is 31

    const colCount = columns.length;
    let currentRow = 1;

    // Title Row
    worksheet.mergeCells(currentRow, 1, currentRow, colCount);
    const titleCell = worksheet.getCell(currentRow, 1);
    titleCell.value = sheetName;
    titleCell.font = { size: 16, bold: true, color: { argb: 'FF7367F0' } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getRow(currentRow).height = 30;
    currentRow++;

    // Recruiter Row
    if (hrName) {
      worksheet.mergeCells(currentRow, 1, currentRow, colCount);
      const hrCell = worksheet.getCell(currentRow, 1);
      hrCell.value = `Recruiter: ${hrName}`;
      hrCell.font = { size: 11, bold: true, color: { argb: 'FF1E293B' } };
      currentRow++;
    }

    // Duration Row
    if (durationText) {
      worksheet.mergeCells(currentRow, 1, currentRow, colCount);
      const durationCell = worksheet.getCell(currentRow, 1);
      durationCell.value = `Duration: ${durationText}`;
      durationCell.font = { size: 10, color: { argb: 'FF475569' } };
      currentRow++;
    }

    // Technology Row
    if (techFilter && techFilter.length > 0 && techText) {
      worksheet.mergeCells(currentRow, 1, currentRow, colCount);
      const techCell = worksheet.getCell(currentRow, 1);
      techCell.value = `Technology: ${techText}`;
      techCell.font = { size: 10, color: { argb: 'FF475569' } };
      currentRow++;
    }

    // Header Row
    const headerRow = worksheet.getRow(currentRow);
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = col.header;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF7367F0' }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
      };
      cell.alignment = getAlignment(col);
    });
    headerRow.height = 25;
    currentRow++;

    // Data Rows
    data.forEach((row, rowIndex) => {
      const dataRow = worksheet.getRow(currentRow);
      const isZebra = rowIndex % 2 === 1;
      const bgColor = isZebra ? 'FFF8FAFC' : 'FFFFFFFF';

      columns.forEach((col, colIndex) => {
        let val = '';
        let customColor = null;

        if (col.formatter) {
          const res = col.formatter(row);
          if (res !== null && typeof res === 'object' && res.value !== undefined) {
            val = res.value;
            customColor = res.color;
          } else {
            val = res;
          }
        } else {
          val = row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : '--';
        }

        let displayVal = String(val)
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>');

        const cell = dataRow.getCell(colIndex + 1);
        cell.value = displayVal;

        if (customColor) {
          cell.font = { color: { argb: customColor }, bold: true };
        } else {
          cell.font = { color: { argb: 'FF334155' } };
        }

        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: bgColor }
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
          right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
        };
        cell.alignment = getAlignment(col);
      });
      currentRow++;
    });

    // Calculate dynamic column widths
    columns.forEach((col, index) => {
      let maxLen = col.header ? col.header.length : 12;
      data.forEach((row) => {
        let val = '';
        if (col.formatter) {
          const res = col.formatter(row);
          val =
            res !== null && typeof res === 'object' && res.value !== undefined ? res.value : res;
        } else {
          val = row[col.key] !== undefined && row[col.key] !== null ? row[col.key] : '--';
        }
        const valLen = String(val).length;
        if (valLen > maxLen) {
          maxLen = valLen;
        }
      });
      worksheet.getColumn(index + 1).width = Math.min(Math.max(maxLen + 2, 12), 60);
    });
  });

  // Generate buffer and save as .xlsx
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  saveAs(blob, `${fileName}.xlsx`);
};
